/**
 * Display-currency conversion for list pricing.
 *
 * USD is the base, the default, and the only currency we actually bill in.
 * Local amounts are indicative conversions so visitors can read the price in a
 * familiar currency; they are not separate price lists.
 *
 * Rates are static and approximate. Refresh them here, or override without a
 * release via VITE_PRICING_FX_RATES — a JSON map of code -> units per 1 USD,
 * e.g. {"AED":3.67,"GBP":0.75}.
 */

export const BASE_CURRENCY = 'USD';

/** Bump when the rate table below is refreshed. */
export const FX_RATES_UPDATED_AT = '2026-09-01';

export type CurrencyMeta = {
  code: string;
  symbol: string;
  name: string;
  /** Locale used for digit grouping. */
  locale: string;
  /** Units of this currency per 1 USD. */
  usdRate: number;
  /** Fraction digits when showing a converted amount. */
  decimals: number;
};

const CURRENCY_TABLE: CurrencyMeta[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US', usdRate: 1, decimals: 2 },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', locale: 'en-AE', usdRate: 3.67, decimals: 0 },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', locale: 'en-SA', usdRate: 3.75, decimals: 0 },
  { code: 'QAR', symbol: 'QAR', name: 'Qatari Riyal', locale: 'en-QA', usdRate: 3.64, decimals: 0 },
  { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB', usdRate: 0.75, decimals: 2 },
  { code: 'EUR', symbol: '€', name: 'Euro', locale: 'en-IE', usdRate: 0.86, decimals: 2 },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG', usdRate: 1.29, decimals: 2 },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', locale: 'en-MY', usdRate: 4.2, decimals: 0 },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID', usdRate: 16500, decimals: 0 },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', locale: 'en-PH', usdRate: 57, decimals: 0 },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', locale: 'th-TH', usdRate: 32, decimals: 0 },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', locale: 'vi-VN', usdRate: 26000, decimals: 0 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU', usdRate: 1.52, decimals: 2 },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ', usdRate: 1.68, decimals: 2 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA', usdRate: 1.38, decimals: 2 },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', locale: 'en-ZA', usdRate: 17.5, decimals: 0 },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', locale: 'en-KE', usdRate: 129, decimals: 0 },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG', usdRate: 1500, decimals: 0 },
  { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound', locale: 'en-EG', usdRate: 48, decimals: 0 },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR', usdRate: 5.4, decimals: 0 },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso', locale: 'es-MX', usdRate: 18.5, decimals: 0 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', usdRate: 150, decimals: 0 },
  { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee', locale: 'en-LK', usdRate: 300, decimals: 0 },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', locale: 'en-BD', usdRate: 122, decimals: 0 },
  { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee', locale: 'en-PK', usdRate: 280, decimals: 0 },
];

function readRateOverrides(): Record<string, number> {
  const raw = import.meta.env?.VITE_PRICING_FX_RATES;
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [code, rate] of Object.entries(parsed as Record<string, unknown>)) {
      const n = Number(rate);
      if (Number.isFinite(n) && n > 0) out[code.toUpperCase()] = n;
    }
    return out;
  } catch {
    return {};
  }
}

const RATE_OVERRIDES = readRateOverrides();

export const CURRENCIES: CurrencyMeta[] = CURRENCY_TABLE.map((c) => ({
  ...c,
  usdRate: c.code === BASE_CURRENCY ? 1 : (RATE_OVERRIDES[c.code] ?? c.usdRate),
}));

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

export const BASE_CURRENCY_META = BY_CODE.get(BASE_CURRENCY) as CurrencyMeta;

export function isSupportedCurrency(code: string | null | undefined): boolean {
  return !!code && BY_CODE.has(code.toUpperCase());
}

export function getCurrency(code: string | null | undefined): CurrencyMeta {
  return (code && BY_CODE.get(code.toUpperCase())) || BASE_CURRENCY_META;
}

export function isBaseCurrency(code: string | null | undefined): boolean {
  return getCurrency(code).code === BASE_CURRENCY;
}

/** USD amount -> amount in `code`. */
export function convertFromUsd(amountUsd: number, code: string): number {
  if (!Number.isFinite(amountUsd)) return 0;
  return amountUsd * getCurrency(code).usdRate;
}

/** Letter-based symbols ("AED", "RM", "KSh") need a space; "$" / "€" / "A$" do not. */
function symbolPrefix(meta: CurrencyMeta): string {
  return /[A-Za-z]$/.test(meta.symbol) ? `${meta.symbol} ` : meta.symbol;
}

/** Format a USD amount in `code`: `$19`, `AED 70`, `€16.34`. */
export function formatMoney(amountUsd: number, code: string): string {
  const meta = getCurrency(code);
  if (!Number.isFinite(amountUsd)) return '—';

  const factor = 10 ** meta.decimals;
  const converted = Math.round(convertFromUsd(amountUsd, meta.code) * factor) / factor;
  const abs = Math.abs(converted);
  // Small amounts would round away to nothing in a 0-decimal currency.
  const digits = Number.isInteger(converted) ? 0 : abs < 1 ? 2 : meta.decimals;

  let body: string;
  try {
    body = new Intl.NumberFormat(meta.locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(abs);
  } catch {
    body = abs.toFixed(digits);
  }

  return `${converted < 0 ? '-' : ''}${symbolPrefix(meta)}${body}`;
}

/** "1 USD ≈ AED 3.67" for the conversion disclaimer; null for USD itself. */
export function formatRateLine(code: string): string | null {
  const meta = getCurrency(code);
  if (meta.code === BASE_CURRENCY) return null;
  const digits = meta.usdRate >= 100 ? 0 : 2;
  let rate: string;
  try {
    rate = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(meta.usdRate);
  } catch {
    rate = String(meta.usdRate);
  }
  return `1 ${BASE_CURRENCY} ≈ ${symbolPrefix(meta)}${rate}`;
}

const EUROZONE = [
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT',
  'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK', 'MC', 'AD', 'SM', 'VA',
];

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  AE: 'AED',
  SA: 'SAR',
  QA: 'QAR',
  GB: 'GBP', IM: 'GBP', JE: 'GBP', GG: 'GBP',
  SG: 'SGD',
  MY: 'MYR',
  ID: 'IDR',
  PH: 'PHP',
  TH: 'THB',
  VN: 'VND',
  AU: 'AUD',
  NZ: 'NZD',
  CA: 'CAD',
  ZA: 'ZAR', NA: 'ZAR', LS: 'ZAR',
  KE: 'KES',
  NG: 'NGN',
  EG: 'EGP',
  BR: 'BRL',
  MX: 'MXN',
  JP: 'JPY',
  LK: 'LKR',
  BD: 'BDT',
  PK: 'PKR',
  ...Object.fromEntries(EUROZONE.map((c) => [c, 'EUR'])),
};

/** Currency a visitor in `countryCode` would most likely want (USD when unmapped). */
export function currencyForCountry(countryCode: string | null | undefined): string {
  const cc = countryCode?.trim().toUpperCase();
  if (!cc) return BASE_CURRENCY;
  const code = COUNTRY_TO_CURRENCY[cc];
  return code && isSupportedCurrency(code) ? code : BASE_CURRENCY;
}
